import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox } from "antd";
import PinIcon from "../../../assets/images/Pin.png";
import { CloseIcon, FailedIcon } from "../../../assets/icons";
import "antd/dist/reset.css";
import Loader from "../../Loader/Loader";
import ErrorIconImg from '../../../assets/images/Error.png';
import {
  Actions,
  Btn,
  NextButton,
  Header,
  SubHeader,
  Modal,
  Overlay,
  UploadedFile,
  UploadedFileInfo,
  UploadedFileName,
  CloseButton,
  FormSection,
  SectionTitle,
  FormRow,
  FormField,
  FieldLabel,
  StyledInput,
  StyledSelectField,
  ErrorInput,
  ErrorSelect,
  ErrorRow,
  ErrorRowSelect,
  ErrorIcon,
  ErrorText,
} from "./SendToVAModalStyled";
import { fetchVAFormData, compressAndSendVAFormData } from "../../../service/matter";
import { MAX_SIZE_BYTES } from "../../../utils/constants";
import { 
  validateDocument, 
  createFieldValidations, 
  validateForm, 
  areRequiredFieldsFilled, 
  getInitialFormData, 
  handleSSNInput, 
  prepareFinalFormData,
  filesCompensationCopy,
  VA_BOARD_BUSINESS_LINE,
  VA_COPY_NOTE,
} from "../TabViewUtils";

export default function SendToVAModal({
  onClose,
  onNext,
  selectedDoc,
  setShowSendToVAModal,
  setShowGeneratedInternalFailure,
  initialFormData,
  setMessageModal,
  handleSnackbar
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [benefitClaimTypeOptions, setBenefitClaimTypeOptions] = useState([]);
  const [formData, setFormData] = useState(() => {
    // Ensure all fields are initialized as empty strings
    const baseData = getInitialFormData();
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      return { ...baseData, ...initialFormData };
    }
    return baseData;
  });
  const [errors, setErrors] = useState({});
  // null | 'offer' | 'queued' — the oversized-file branch: offer a
  // compressed-copy submission, then confirm it was queued.
  const [compressStep, setCompressStep] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);

  const fieldValidations = createFieldValidations();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClose = () => {
    setFormData(getInitialFormData());
    onClose();
  };

  const handleFieldChange = (field, value) => {
    handleInputChange(field, value);
  };

  const showExistingUploadModal = () => {
    setMessageModal({
      isOpen: true,
      title: "VA submission is in progress",
      message: "Document already has an active VA submission in progress",
      buttonText: "Close",
      icon: <FailedIcon />,
      variant: "failure",
      onClose: () => {
        setMessageModal(prev => ({ ...prev, isOpen: false }));
      },
      onClick: () => {
        setMessageModal(prev => ({ ...prev, isOpen: false }));
      }
    });
    setShowSendToVAModal(false);
  };

  const handleApiError = (error) => {
    console.error("Error fetching categories:", error);
    if (error?.response?.data?.existing_upload_id) {
      showExistingUploadModal();
    } else {
      handleSnackbar(error?.response?.data?.error);
      setShowSendToVAModal(false);
    }
  };

  const updateFormDataFromApi = (apiData) => {
    const baseData = getInitialFormData();
    const mergedData = { ...baseData, ...apiData };
    setFormData(mergedData);
  };

  const shouldFetchFormData = () => {
    return !initialFormData || Object.keys(initialFormData).length === 0;
  };

  const shouldFetchOptions = () => {
    return benefitClaimTypeOptions.length === 0;
  };

  const onSubmitNext = async () => {
    try {
      // Prevent multiple submissions while loading
      if (isLoading) return;
      
      const validationErrors = validateForm(formData, fieldValidations);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) return;
      const finalFormData = prepareFinalFormData(formData, selectedDoc);
      setIsLoading(true);
      // Wrap PDF validation in a more robust error handler
      let validationResult;
      try {
        validationResult = await validateDocument(selectedDoc);
      } catch (pdfError) {
        console.error("PDF validation error:", pdfError);
        // If PDF validation fails, we'll still proceed but log the error
        // This prevents the export token error from crashing the app
        validationResult = { isValid: true, reason: 'validation_skipped' };
      }
      if (!validationResult?.isValid) {
        if (validationResult?.reason === "size") {
          // Over the VA's 100 MB limit: offer the compressed-copy path
          // instead of the dead-end failure modal.
          setPendingFormData(finalFormData);
          setCompressStep("offer");
          setIsLoading(false);
          return;
        }
        setShowSendToVAModal(false);
        setShowGeneratedInternalFailure(true);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      onNext(finalFormData);
    } catch (error) {
      console.error("Error in onSubmitNext:", error);
      setIsLoading(false);
      handleSnackbar("An error occurred while processing your request. Please try again.");
    }
  };
  

  const handleCompressConfirm = async () => {
    if (isLoading || !pendingFormData) return;
    setIsLoading(true);
    try {
      await compressAndSendVAFormData(pendingFormData);
      setCompressStep("queued");
    } catch (error) {
      console.error("Error queueing compress-and-submit:", error);
      if (error?.response?.data?.existing_upload_id) {
        showExistingUploadModal();
      } else {
        handleSnackbar(
          error?.response?.data?.error ||
            "Could not start compression. Please try again."
        );
        setShowSendToVAModal(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchVADatas = async () => {
      setIsLoading(true);
      try {
        const documentId = selectedDoc?.unique_id?.replace(/^D/, '');
        const response = await fetchVAFormData(documentId);
        
        if (shouldFetchFormData()) {
          const apiData = response?.data?.prefilled_data || {};
          updateFormDataFromApi(apiData);
        }
        
        setBenefitClaimTypeOptions(response?.data?.business_line_options);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (shouldFetchFormData() || shouldFetchOptions()) {
      fetchVADatas();
    }
  }, [initialFormData, benefitClaimTypeOptions.length]);

  // Inject global CSS for dropdown z-index
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ant-select-dropdown {
        z-index: 9999 !important;
      }
      .ant-select-arrow {
        top: 50% !important;
        transform: translateY(-50%) !important;
        margin-top: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        style.remove();
      }
    };
  }, []);
  const limitMb = Math.round(MAX_SIZE_BYTES / (1024 * 1024));
  const fileMb = selectedDoc?.size
    ? Math.round(selectedDoc.size / (1024 * 1024))
    : null;

  if (compressStep === "offer") {
    return (
      <Overlay>
        <Modal>
          {isLoading && <Loader />}
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
          <Header>File is over the Veterans Affairs size limit</Header>
          <SubHeader>
            {fileMb
              ? `This file is about ${fileMb} MB; Veterans Affairs accepts up to ${limitMb} MB.`
              : `Veterans Affairs accepts files up to ${limitMb} MB.`}{" "}
            WHG Docs can create a compressed copy in this matter and submit
            that copy instead. Your original file is not changed.
          </SubHeader>
          <UploadedFile>
            <UploadedFileInfo>
              <img src={PinIcon} alt="Attachment" width={24} height={24} />
              <UploadedFileName>{selectedDoc.name}</UploadedFileName>
            </UploadedFileInfo>
          </UploadedFile>
          <Actions>
            <Btn onClick={handleClose}>Cancel</Btn>
            <NextButton onClick={handleCompressConfirm} disabled={isLoading}>
              Compress & Submit
            </NextButton>
          </Actions>
        </Modal>
      </Overlay>
    );
  }

  if (compressStep === "queued") {
    return (
      <Overlay>
        <Modal>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
          <Header>Compressing and submitting</Header>
          <SubHeader>
            The compressed copy will be submitted to Veterans Affairs
            automatically — no further action needed. You can check the doc
            upload status from Dashboard/Home screen; the copy appears in
            the matter next to the original.
            {filesCompensationCopy(pendingFormData) && ` ${VA_COPY_NOTE}`}
          </SubHeader>
          <Actions>
            <NextButton onClick={handleClose}>Close</NextButton>
          </Actions>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay>
      <Modal>
        {isLoading && <Loader />}
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>

        <Header>Sent file to Veterans Affairs</Header>

        {/* Veteran Information Section */}
        <FormSection>
          <SectionTitle>Veteran Information</SectionTitle>
          <FormRow>
            <FormField>
              <FieldLabel>First Name</FieldLabel>
              {errors.veteranFirstName ? (
                <>
                  <ErrorInput
                    type="text"
                    placeholder="Enter first name"
                    value={formData.veteranFirstName}
                    onChange={e => handleFieldChange('veteranFirstName', e.target.value)}
                  />
                  <ErrorRow>
                    <ErrorIcon src={ErrorIconImg} alt="error" />
                    <ErrorText>{errors.veteranFirstName}</ErrorText>
                  </ErrorRow>
                </>
              ) : (
                <StyledInput
                  type="text"
                  placeholder="Enter first name"
                  value={formData.veteranFirstName}
                  onChange={e => handleFieldChange('veteranFirstName', e.target.value)}
                />
              )}
            </FormField>
            <FormField>
              <FieldLabel>Last Name</FieldLabel>
              {errors.veteranLastName ? (
                <>
                  <ErrorInput
                    type="text"
                    placeholder="Enter last name"
                    value={formData.veteranLastName}
                    onChange={e => handleFieldChange('veteranLastName', e.target.value)}
                  />
                  <ErrorRow>
                    <ErrorIcon src={ErrorIconImg} alt="error" />
                    <ErrorText>{errors.veteranLastName}</ErrorText>
                  </ErrorRow>
                </>
              ) : (
                <StyledInput
                  type="text"
                  placeholder="Enter last name"
                  value={formData.veteranLastName}
                  onChange={e => handleFieldChange('veteranLastName', e.target.value)}
                />
              )}
            </FormField>
          </FormRow>
          <FormRow>
            <FormField>
              <FieldLabel>Veteran File Number/ SSN</FieldLabel>
              {errors.fileNumber ? (
                <>
                  <ErrorInput
                    type="text"
                    placeholder="Enter SSN"
                    value={formData.fileNumber}
                    onChange={e => handleSSNInput(e, handleInputChange)}
                  />
                  <ErrorRow>
                    <ErrorIcon src={ErrorIconImg} alt="error" />
                    <ErrorText>{errors.fileNumber}</ErrorText>
                  </ErrorRow>
                </>
              ) : (
                <StyledInput
                  type="text"
                  placeholder="Enter SSN"
                  value={formData.fileNumber}
                  onChange={e => handleSSNInput(e, handleInputChange)}
                />
              )}
            </FormField>
            <FormField>
              <FieldLabel>Zip Code</FieldLabel>
              {errors.zipCode ? (
                <>
                  <ErrorInput
                    type="text"
                    placeholder="Enter Zip Code"
                    value={formData.zipCode}
                    onChange={e => handleFieldChange('zipCode', e.target.value)}
                  />
                  <ErrorRow>
                    <ErrorIcon src={ErrorIconImg} alt="error" />
                    <ErrorText>{errors.zipCode}</ErrorText>
                  </ErrorRow>
                </>
              ) : (
                <StyledInput
                  type="text"
                  placeholder="Enter Zip Code"
                  value={formData.zipCode}
                  onChange={e => handleFieldChange('zipCode', e.target.value)}
                />
              )}
            </FormField>
          </FormRow>
        </FormSection>

        {/* Submission Information Section */}
        <FormSection>
          <SectionTitle>Submission Information</SectionTitle>
          <FormRow>
            <FormField>
              <FieldLabel>Organization</FieldLabel>
              {errors.source ? (
                <>
                  <ErrorInput
                    type="text"
                    value={formData.source}
                    onChange={e => handleFieldChange('source', e.target.value)}
                    disabled={true}
                  />
                  <ErrorRow>
                    <ErrorIcon src={ErrorIconImg} alt="error" />
                    <ErrorText>{errors.source}</ErrorText>
                  </ErrorRow>
                </>
              ) : (
                <StyledInput
                  type="text"
                  value={formData.source}
                  onChange={e => handleFieldChange('source', e.target.value)}
                  disabled={true}
                />
              )}
            </FormField>
            <FormField>
              <FieldLabel>Document Type</FieldLabel>
              <StyledInput
                type="text"
                value={formData.docType || ''}
                placeholder="Optional - e.g. 21-22a"
                maxLength={10}
                onChange={e => handleFieldChange('docType', e.target.value)}
              />
            </FormField>
            <FormField>
              <FieldLabel>Benefit Claim Type</FieldLabel>
              {errors.businessLine ? (
                <>
                  <ErrorSelect
                    placeholder="Choose Claim Type"
                    value={formData.businessLine}
                    onChange={value => handleFieldChange('businessLine', value)}
                    options={benefitClaimTypeOptions}
                    getPopupContainer={triggerNode => triggerNode.parentNode}
                  />
                  <ErrorRowSelect>
                    <ErrorIcon src={ErrorIconImg} alt="error" />
                    <ErrorText>{errors.businessLine}</ErrorText>
                  </ErrorRowSelect>
                </>
              ) : (
                <StyledSelectField
                  placeholder="Choose Claim Type"
                  value={formData.businessLine ? formData.businessLine : null}
                  onChange={value => handleFieldChange('businessLine', value)}
                  options={benefitClaimTypeOptions}
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                />
              )}
            </FormField>
          </FormRow>
          {formData.businessLine === VA_BOARD_BUSINESS_LINE && (
            <FormRow>
              <Checkbox
                id="va-also-file-with-compensation"
                checked={formData.alsoFileWithCompensation !== false}
                onChange={e => handleFieldChange('alsoFileWithCompensation', e.target.checked)}
              >
                Also file a copy with Compensation
              </Checkbox>
            </FormRow>
          )}
        </FormSection>
        <SectionTitle>File Selected</SectionTitle>

          <UploadedFile>
            <UploadedFileInfo>
              <img src={PinIcon} alt="Attachment" width={24} height={24} />
              <UploadedFileName>{selectedDoc.name}</UploadedFileName>
            </UploadedFileInfo>
            {/* <StyledPreviewIcon 
                src={PreviewIcon} 
                alt="Preview" 
                onClick={() => {
                }}
              /> */}
          </UploadedFile>

        <Actions>
          <Btn onClick={handleClose}>Cancel</Btn>
          <NextButton
            onClick={onSubmitNext}
            disabled={!areRequiredFieldsFilled(formData) || isLoading}
          >
            Next
          </NextButton>
        </Actions>
      </Modal>
    </Overlay>
  );
}

SendToVAModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  selectedDoc: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    unique_id: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    s3_path: PropTypes.string.isRequired,
  }),
  setShowSendToVAModal: PropTypes.func.isRequired,
  setShowGeneratedInternalFailure: PropTypes.func.isRequired,
  initialFormData: PropTypes.object,
  setMessageModal: PropTypes.func.isRequired,
  handleSnackbar: PropTypes.func.isRequired,
};
